import {isPlatformBrowser} from "@angular/common";
import {
  afterNextRender,
  Component,
  DOCUMENT,
  inject,
  input,
  linkedSignal,
  LOCALE_ID,
  PLATFORM_ID,
  signal,
} from "@angular/core";
import {disabled, email, form, FormField, FormRoot, required} from "@angular/forms/signals";
import type {SafeResourceUrl} from "@angular/platform-browser";
import {DomSanitizer} from "@angular/platform-browser";
import Cap from "@cap.js/widget";
import {firstValueFrom} from "rxjs";
import {WEB_APP_CONFIG} from "../../../common/app-config";
import {Header} from "../../layout/header/header";
import {ContactForm} from "../../models/ContactForm/ContactForm";
import {ContactFormApi} from "../../services/api/contact-form-api";

@Component({
  selector: "app-contact-us",
  templateUrl: "./contact-us.html",
  styleUrl: "./contact-us.scss",
  imports: [
    Header,
    FormField,
    FormRoot,
  ],
})
export default class ContactUs {
  private readonly contactFormApi = inject(ContactFormApi);

  public readonly subject = input<string>();

  protected readonly contactFormData = linkedSignal(() => ContactForm.createEmpty(this.subject()));
  protected readonly contactForm = form(this.contactFormData, schema => {
    required(schema.sender.name);
    disabled(schema.sender.name, {when: ctx => ctx.state.submitting()});
    required(schema.sender.email);
    email(schema.sender.email);
    disabled(schema.sender.email, {when: ctx => ctx.state.submitting()});
    required(schema.subject);
    disabled(schema.subject, {when: ctx => ctx.state.submitting()});
    required(schema.message);
    disabled(schema.message, {when: ctx => ctx.state.submitting()});
  }, {
    submission: {
      action: async (tree) => this.submitForm(tree().value()),
    },
  });
  protected readonly contactFormSubmission = signal<undefined | "sent" | "error">(undefined);

  protected readonly mapsEmbedSrc?: SafeResourceUrl;

  private cap?: Cap;

  // eslint-disable-next-line max-statements
  public constructor() {
    const webApiConfig = inject(WEB_APP_CONFIG);
    const sanitizer = inject(DomSanitizer);
    const appleMapsSettings = webApiConfig.appleMapsEmbeddingSettings;
    if (appleMapsSettings) {
      const locale = inject(LOCALE_ID);
      // URL Builder: https://developer.apple.com/maps/create-a-map
      const mapsBaseUrl = new URL("https://embed.apple-mapkit.com/v1/embed");
      mapsBaseUrl.searchParams.append("center", `${appleMapsSettings.map.center.latitude},${appleMapsSettings.map.center.longitude}`);
      mapsBaseUrl.searchParams.append("cameraDistance", appleMapsSettings.map.cameraDistance);
      mapsBaseUrl.searchParams.append("annotations", JSON.stringify(appleMapsSettings.map.placeIds.map(placeId => ({place: placeId}))));
      mapsBaseUrl.searchParams.append("language", locale);
      mapsBaseUrl.searchParams.append("colorScheme", "adaptive");
      mapsBaseUrl.searchParams.append("t", "hybrid");
      mapsBaseUrl.searchParams.append("token", appleMapsSettings.token);
      this.mapsEmbedSrc = sanitizer.bypassSecurityTrustResourceUrl(mapsBaseUrl.toString());
    } else {
      this.mapsEmbedSrc = sanitizer.bypassSecurityTrustResourceUrl("https://www.google.com/maps/d/embed?mid=1Rf_wPyWO8pvhBjJZePrikRUD4drjf_9C");
    }
    const capSettings = webApiConfig.cap;
    if (capSettings !== undefined) {
      const initCap = (): void => {
        window.CAP_CUSTOM_WASM_URL = capSettings.wasmUrl;
        this.cap = new Cap({apiEndpoint: capSettings.endpointUrl});
      };
      if (isPlatformBrowser(inject(PLATFORM_ID))) {
        initCap();
      } else {
        const doc = inject(DOCUMENT);
        const preconnect = doc.createElement("link");
        preconnect.rel = "preconnect";
        preconnect.href = capSettings.baseUrl;
        doc.head.appendChild(preconnect);
        afterNextRender(initCap);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  private async sendEmail(data: ContactForm): Promise<void> {
    const solveResult = await this.cap?.solve();
    if (solveResult?.success === false) throw new Error("Token solver failed. Are you a bot?");
    if (solveResult?.token !== undefined) data.noBotVerification = solveResult.token;
    console.info("CAP solver result:", solveResult);
    await firstValueFrom(this.contactFormApi.submit(data));
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  protected async submitForm(data: ContactForm): Promise<void> {
    try {
      await this.sendEmail(data);
      this.contactFormData.set(ContactForm.createEmpty());
      this.contactForm().reset();
      this.contactFormSubmission.set("sent");
    } catch {
      this.contactFormSubmission.set("error");
    }
  }
}
